import React, { useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import CrmContactLink from '@/components/crm/CrmContactLink';
import { CommercialLifecycleTimeline } from '@/components/commercial-lifecycle/CommercialLifecycleTimeline';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Typography,
  Select,
  Tag,
  Progress,
  App,
  Tooltip,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  CalculatorOutlined,
  UnorderedListOutlined,
  PlusOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useOpportunity,
  useOpportunityActivities,
  useUpdateOpportunityStage,
  useMarkOpportunityWon,
  useMarkOpportunityLost,
  useConvertOpportunityToProject,
} from '@/hooks/useOpportunities';
import { useAuthStore } from '@/store/authStore';
import CrmActivityTimeline from '@/components/crm/activities/CrmActivityTimeline';
import CrmActivityForm from '@/components/crm/activities/CrmActivityForm';
import { useQuotations } from '@/hooks/useQuotation';
import { useSalesOrders } from '@/hooks/useSalesOrder';
import { useProjectsForSelection } from '@/hooks/useProject';
import { useBOQs } from '@/hooks/useBOQ';
import { useTradingRentalEstimations } from '@/hooks/useTradingRentalEstimation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import type { BOQHeader } from '@/services/boq.service';
import DocumentList from '@/components/documents/DocumentList';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import DocumentUpload from '@/components/documents/DocumentUpload';
import orderReviewChecklistService from '@/services/orderReviewChecklist.service';
import { PendingApproverText } from '@/components/approval/PendingApproverText';
import { usePendingApproversMap } from '@/hooks/usePendingApproversMap';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import {
  isManufacturingRevenueServiceType,
  resolveRstFromList,
} from '@/utils/revenueServiceTypeRouting';
import { SalesTeamTransferModal } from '@/components/sales/SalesTeamTransferModal';
import { TransferHistoryPanel } from '@/components/sales/TransferHistoryPanel';
import leadLifecycleService from '@/services/leadLifecycle.service';
import { useInstalledSolutionPacks } from '@/hooks/useInstalledSolutionPacks';
import {
  EPC_COMMERCIAL_PACK_KEYS,
} from '@/config/solutionPackNavGates';
import { pursuitPath } from '@/components/epc/commercial-ux/resolvePursuitOpportunityId';
import { EpcOpportunityWorkspace } from '@/components/epc/commercial-ux/EpcOpportunityWorkspace';
import { OPPORTUNITY_STAGES, canonicalOpportunityStage } from '@/constants/opportunity-stages';

const { Title, Text } = Typography;
const { TextArea } = Input;

const OpportunityDetailPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { installedPackKeys } = useInstalledSolutionPacks();
  const hasEpcCommercialPack =
    installedPackKeys == null ||
    EPC_COMMERCIAL_PACK_KEYS.some((k) => installedPackKeys.includes(k));
  const [wonOpen, setWonOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [winReason, setWinReason] = useState('');
  const [lossReason, setLossReason] = useState('');
  const [projectOpen, setProjectOpen] = useState(false);
  const [projectId, setProjectId] = useState<string>('');
  const [transferOpen, setTransferOpen] = useState(false);
  const { resolve: resolveUserDisplay } = useUserDisplayResolver();
  const { data: opp, isLoading } = useOpportunity(id);
  const { data: activities = [], isLoading: actLoading } = useOpportunityActivities(id);
  const updateStage = useUpdateOpportunityStage();
  const markWon = useMarkOpportunityWon();
  const markLost = useMarkOpportunityLost();
  const convertProject = useConvertOpportunityToProject();
  const { user } = useAuthStore();
  const canLogActivity = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.create'));
  const canScheduleDemo = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.demo.create'));

  const { data: intelligence } = useQuery({
    queryKey: ['opp-intelligence', id],
    queryFn: () => leadLifecycleService.getOpportunityIntelligence(id!),
    enabled: !!id,
  });

  const { data: oppCheckpoint } = useQuery({
    queryKey: ['opp-checkpoint', id],
    queryFn: () => leadLifecycleService.getOpportunityCheckpoint(id!),
    enabled: !!id,
  });

  const { data: projectOptions = [] } = useProjectsForSelection();

  const { data: linkedBoqsData, isLoading: boqsLoading } = useBOQs(
    id ? { opportunity_id: id } : undefined,
    { enabled: !!id }
  );

  const { data: oppQuotations = [], isLoading: quotationsLoading } = useQuotations(
    id ? { opportunity_id: id, is_latest_revision: true } : undefined,
    { enabled: !!id }
  );
  const { data: oppSalesOrders = [], isLoading: salesOrdersLoading } = useSalesOrders(
    id ? { opportunity_id: id } : undefined,
    { enabled: !!id }
  );
  const linkedBoqs: BOQHeader[] = Array.isArray(linkedBoqsData)
    ? linkedBoqsData
    : (linkedBoqsData as { data?: BOQHeader[] })?.data || [];
  const { data: linkedTradingRentalEstimations = [], isLoading: tradingRentalLoading } =
    useTradingRentalEstimations(id ? { opportunity_id: id } : undefined);
  const { data: linkedOrcs = [], isLoading: orcLoading } = useQuery({
    queryKey: ['order-review-checklist', 'opportunity', id],
    queryFn: () => orderReviewChecklistService.listByOpportunity(id!),
    enabled: !!id,
  });
  const orcPendingInstanceIds = useMemo(
    () =>
      linkedOrcs
        .filter((x) => x.approval_instance_id && x.approval_status === 'Pending')
        .map((x) => x.approval_instance_id as string),
    [linkedOrcs]
  );
  const { labels: orcPendingApproverLabels } = usePendingApproversMap(orcPendingInstanceIds);
  const createOrcMutation = useMutation({
    mutationFn: () => orderReviewChecklistService.createFromOpportunity(id!),
    onSuccess: (created) => {
      message.success('Order Review Checklist created');
      queryClient.invalidateQueries({ queryKey: ['order-review-checklist', 'opportunity', id] });
      navigate(`/growth/order-review-checklists/${created.id}`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed to create ORC'),
  });

  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const revenueServiceTypeLabel = useMemo(() => {
    if (!opp?.revenue_service_type_id) return '—';
    const r = revenueServiceTypes.find((x) => x.id === opp.revenue_service_type_id);
    return r ? `${r.service_code} — ${r.service_name}` : '—';
  }, [opp?.revenue_service_type_id, revenueServiceTypes]);
  const latestOrc = linkedOrcs[0];
  const activeOrc = linkedOrcs.find(
    (x) => x.review_status !== 'Rejected' && x.review_status !== 'Cancelled'
  );
  const activeReviewHint = 'Active review already exists; open/cancel existing first.';
  const latestApprovedOrc = linkedOrcs.find((x) => x.review_status === 'Approved' && x.approval_status === 'Approved');
  const opportunityRst = useMemo(
    () => resolveRstFromList(opp?.revenue_service_type_id, revenueServiceTypes),
    [opp?.revenue_service_type_id, revenueServiceTypes]
  );
  const hasRst = Boolean(opportunityRst);
  const isManufacturingRst = isManufacturingRevenueServiceType(opportunityRst);
  const canImportEstimation = isManufacturingRst && Boolean(latestApprovedOrc);
  const rstMissingHint = 'Set Revenue service type on the opportunity before creating estimations.';

  const quotationColumns = useMemo(
    () => [
      {
        title: '#',
        key: 'qn',
        render: (_: unknown, r: { quotation_number?: string }) => r.quotation_number ?? '—',
      },
      {
        title: 'Name',
        key: 'nm',
        ellipsis: true,
        render: (_: unknown, r: { quotation_name?: string }) => r.quotation_name ?? '—',
      },
      {
        title: 'Status',
        key: 'st',
        width: 110,
        render: (_: unknown, r: { status?: string }) => (r.status ? <Tag>{r.status}</Tag> : '—'),
      },
      {
        title: 'Total',
        key: 'tot',
        render: (_: unknown, r: { grand_total?: number; currency?: string }) =>
          `${Number(r.grand_total ?? 0).toLocaleString()}${r.currency ? ` ${r.currency}` : ''}`,
      },
      {
        title: '',
        key: 'op',
        width: 90,
        render: (_: unknown, r: { id: string }) => (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/growth/quotations/${r.id}`)}>
            Open
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const salesOrderColumns = useMemo(
    () => [
      {
        title: 'SO #',
        key: 'so',
        render: (_: unknown, r: { sales_order_number?: string }) => r.sales_order_number ?? '—',
      },
      {
        title: 'Status',
        key: 'os',
        width: 120,
        render: (_: unknown, r: { order_status?: string }) =>
          r.order_status ? <Tag>{r.order_status}</Tag> : '—',
      },
      {
        title: 'Total',
        key: 'gt',
        render: (_: unknown, r: { grand_total?: number; currency?: string }) =>
          `${Number(r.grand_total ?? 0).toLocaleString()}${r.currency ? ` ${r.currency}` : ''}`,
      },
      {
        title: '',
        key: 'op',
        width: 90,
        render: (_: unknown, r: { id: string }) => (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/growth/orders/${r.id}`)}>
            Open
          </Button>
        ),
      },
    ],
    [navigate]
  );

  if (!id) return null;

  return (
    <div style={{ padding: 24 }}>
      {id ? <AiPageContextSync module="sales" docType="Opportunity" docId={id} /> : null}
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/opportunities')}>
          Pipeline
        </Button>
      </Space>

      <Spin spinning={isLoading}>
        {!opp ? (
          <Text type="secondary">Not found.</Text>
        ) : (
          <>
            <Title level={3}>
              {opp.opportunity_name}{' '}
              <Text type="secondary" style={{ fontSize: 16 }}>
                {opp.opportunity_number}
              </Text>
            </Title>
            <CommercialLifecycleTimeline entityType="opportunity" entityId={id!} />
            {hasEpcCommercialPack ? <EpcOpportunityWorkspace opportunity={opp} /> : null}
            <Space style={{ marginBottom: 16 }} wrap>
              {hasEpcCommercialPack && id ? (
                <Button type="default" onClick={() => navigate(pursuitPath(id))}>
                  Open pursuit
                </Button>
              ) : null}
              {isManufacturingRst && (
                <>
                  <Tooltip
                    title={
                      !hasRst
                        ? rstMissingHint
                        : !canImportEstimation
                          ? 'Approved Order Review Checklist is required before BOQ import.'
                          : undefined
                    }
                  >
                    <span>
                      <Button
                        type="primary"
                        icon={<CalculatorOutlined />}
                        disabled={!canImportEstimation}
                        onClick={() => navigate(`/growth/estimations/boq/import?opportunity_id=${opp.id}`)}
                      >
                        Estimation Import (Excel)
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !hasRst
                        ? rstMissingHint
                        : activeOrc
                          ? activeReviewHint
                          : undefined
                    }
                  >
                    <span>
                      <Button
                        icon={<FileTextOutlined />}
                        loading={createOrcMutation.isPending}
                        disabled={!hasRst || !!activeOrc}
                        onClick={() => createOrcMutation.mutate()}
                      >
                        Create Order Review Checklist
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
              {!isManufacturingRst && hasRst && (
                <Button
                  type="primary"
                  icon={<CalculatorOutlined />}
                  onClick={() =>
                    navigate(`/growth/estimations/trading-rental/create?opportunity_id=${opp.id}`)
                  }
                >
                  Create Trading & Rental Estimation
                </Button>
              )}
              {!hasRst && (
                <Tooltip title={rstMissingHint}>
                  <span>
                    <Button type="primary" icon={<CalculatorOutlined />} disabled>
                      Set revenue service type to continue
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                icon={<FileTextOutlined />}
                onClick={() => navigate(`/growth/quotations?opportunity_id=${opp.id}`)}
              >
                Quotations
              </Button>
              <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>
                Transfer team
              </Button>
            </Space>
            <SalesTeamTransferModal
              open={transferOpen}
              onClose={() => setTransferOpen(false)}
              entityType="Opportunity"
              entityId={opp.id}
              entityLabel={opp.opportunity_number}
              currentTeamName={opp.sales_team_name}
              onSuccess={() => queryClient.invalidateQueries()}
            />
            <TransferHistoryPanel entityType="Opportunity" entityId={opp.id} />

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={14}>
                <Card title="Overview" style={{ marginBottom: 16 }}>
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Customer">{opp.customer_name}</Descriptions.Item>
                    <Descriptions.Item label="Contact">{opp.contact_person || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Email">
                      <CrmContactLink kind="email" value={opp.email} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone">
                      <CrmContactLink kind="phone" value={opp.phone} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Sales team">{opp.sales_team_name || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Stage">
                      <Tag>{opp.stage}</Tag> / {opp.status}
                    </Descriptions.Item>
                    <Descriptions.Item label="Probability">
                      <Progress percent={Number(opp.probability || 0)} size="small" />
                    </Descriptions.Item>
                    <Descriptions.Item label="Estimated value">
                      {(opp.estimated_value != null ? Number(opp.estimated_value) : 0).toLocaleString()}{' '}
                      {opp.currency || ''}
                    </Descriptions.Item>
                    <Descriptions.Item label="Expected revenue">
                      {opp.expected_revenue != null ? Number(opp.expected_revenue).toLocaleString() : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Close date">
                      {opp.expected_close_date
                        ? dayjs(opp.expected_close_date).format('DD MMM YYYY')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Opportunity type">{opp.opportunity_type || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Industry">{opp.industry || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Revenue service type">{revenueServiceTypeLabel}</Descriptions.Item>
                    <Descriptions.Item label="ORC number">{latestOrc?.orc_number || '—'}</Descriptions.Item>
                    <Descriptions.Item label="ORC approval status">{latestOrc?.approval_status || '—'}</Descriptions.Item>
                    <Descriptions.Item label="ORC risk level">{latestOrc?.overall_risk_rating || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Latest ORC review date">
                      {latestOrc?.review_date ? dayjs(latestOrc.review_date).format('DD MMM YYYY') : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Project type">{opp.project_type || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Duration (mo)">{opp.project_duration_months ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="Competitors">
                      <Text style={{ whiteSpace: 'pre-wrap' }}>{opp.competitors || '—'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Requirements">
                      <Text style={{ whiteSpace: 'pre-wrap' }}>{opp.requirements || '—'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Sales owner">
                      {opp.salesOwnerUser
                        ? `${opp.salesOwnerUser.first_name} ${opp.salesOwnerUser.last_name || ''}`
                        : resolveUserDisplay(opp.sales_owner, 'Unknown user')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Project link">
                      {opp.is_converted_to_project && opp.project_id ? (
                        <Button type="link" onClick={() => navigate(`/prj/projects/${opp.project_id}/edit`)}>
                          Open project
                        </Button>
                      ) : (
                        '—'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Notes">
                      <Text style={{ whiteSpace: 'pre-wrap' }}>{opp.notes || '—'}</Text>
                    </Descriptions.Item>
                  </Descriptions>

                  <Space wrap style={{ marginTop: 16 }}>
                    <Select
                      value={canonicalOpportunityStage(opp.stage)}
                      style={{ minWidth: 200 }}
                      options={OPPORTUNITY_STAGES.map((s) => ({
                        label: s === 'Won' ? 'Closed won' : s === 'Lost' ? 'Closed lost' : s,
                        value: s,
                      }))}
                      onChange={(st) => updateStage.mutate({ id: opp.id, stage: st })}
                    />
                    <Button onClick={() => setWonOpen(true)} disabled={opp.status !== 'Open'}>
                      Mark won
                    </Button>
                    <Button danger onClick={() => setLostOpen(true)} disabled={opp.status !== 'Open'}>
                      Mark lost
                    </Button>
                    <Button onClick={() => setProjectOpen(true)} disabled={opp.is_converted_to_project}>
                      Convert to project
                    </Button>
                  </Space>
                </Card>

                <Card title="Documents" style={{ marginBottom: 16 }}>
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <DocumentUpload entityType="Opportunity" entityId={opp.id} />
                    <DocumentList entityType="Opportunity" entityId={opp.id} />
                  </Space>
                  <InheritedAttachmentsCard trailDomain="sales" entityType="Opportunity" entityId={opp.id} />
                </Card>

                <Card
                  title="Linked BOQs"
                  loading={boqsLoading}
                  style={{ marginBottom: 16 }}
                  extra={
                    <Button
                      size="small"
                      icon={<UnorderedListOutlined />}
                      onClick={() => navigate(`/growth/estimations/boq?opportunity_id=${opp.id}`)}
                    >
                      Full BOQ list
                    </Button>
                  }
                >
                  {linkedBoqs.length === 0 ? (
                    <Text type="secondary">
                      {!hasRst
                        ? rstMissingHint
                        : isManufacturingRst
                          ? 'No BOQs linked yet. Create and approve an Order Review Checklist, then use Estimation Import (Excel).'
                          : 'BOQ estimation applies to Manufacturing Services and Specialized Fabrication and Welding only. Use Trading & Rental Estimation for this opportunity.'}
                    </Text>
                  ) : (
                    <Table
                      size="small"
                      rowKey="id"
                      pagination={false}
                      dataSource={linkedBoqs}
                      columns={[
                        { title: 'BOQ #', dataIndex: 'boq_number', key: 'bn' },
                        {
                          title: 'Name',
                          dataIndex: 'boq_name',
                          key: 'nm',
                          ellipsis: true,
                        },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          key: 'st',
                          width: 100,
                          render: (s: string) => (s ? <Tag>{s}</Tag> : '—'),
                        },
                        {
                          title: '',
                          key: 'open',
                          width: 90,
                          render: (_: unknown, r: BOQHeader) => (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0 }}
                              onClick={() => navigate(`/growth/estimations/boq/edit/${r.id}`)}
                            >
                              Open
                            </Button>
                          ),
                        },
                      ]}
                    />
                  )}
                </Card>

                <Card
                  title="Order Review Checklists"
                  loading={orcLoading}
                  style={{ marginBottom: 16 }}
                  extra={
                    <Button
                      size="small"
                      icon={<UnorderedListOutlined />}
                      onClick={() => navigate('/growth/order-review-checklists')}
                    >
                      Register
                    </Button>
                  }
                >
                  {linkedOrcs.length === 0 ? (
                    <Text type="secondary">
                      {!hasRst
                        ? rstMissingHint
                        : isManufacturingRst
                          ? 'No Order Review Checklist yet. Create and approve an ORC before BOQ estimation import.'
                          : 'Order Review Checklist is not used for this revenue service type. Use Trading & Rental Estimation instead.'}
                    </Text>
                  ) : (
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={linkedOrcs}
                      pagination={false}
                      columns={[
                        {
                          title: 'ORC #',
                          dataIndex: 'orc_number',
                          render: (v: string, r: any) => (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0 }}
                              onClick={() => navigate(`/growth/order-review-checklists/${r.id}`)}
                            >
                              {v}
                            </Button>
                          ),
                        },
                        { title: 'Risk', dataIndex: 'overall_risk_rating', width: 120, render: (v: string) => v || '—' },
                        { title: 'Review status', dataIndex: 'review_status', width: 150, render: (v: string) => <Tag>{v}</Tag> },
                        {
                          title: 'Approval',
                          dataIndex: 'approval_status',
                          width: 130,
                          render: (v: string, r: { approval_instance_id?: string | null }) => (
                            <div>
                              <Tag color="blue">{v}</Tag>
                              {r.approval_instance_id && v === 'Pending' ? (
                                <PendingApproverText
                                  compact
                                  pendingApproverLabel={orcPendingApproverLabels[r.approval_instance_id]}
                                />
                              ) : null}
                            </div>
                          ),
                        },
                        {
                          title: 'Review date',
                          dataIndex: 'review_date',
                          width: 140,
                          render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
                        },
                      ]}
                    />
                  )}
                </Card>

                <Card
                  title="Trading/Rental estimations"
                  loading={tradingRentalLoading}
                  style={{ marginBottom: 16 }}
                  extra={
                    <Space>
                      {!isManufacturingRst && hasRst && (
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() =>
                            navigate(`/growth/estimations/trading-rental/create?opportunity_id=${opp.id}`)
                          }
                        >
                          Create
                        </Button>
                      )}
                      <Button
                        size="small"
                        icon={<CalculatorOutlined />}
                        onClick={() => navigate(`/growth/estimations/trading-rental`)}
                      >
                        Full list
                      </Button>
                    </Space>
                  }
                >
                  {linkedTradingRentalEstimations.length === 0 ? (
                    <Text type="secondary">
                      {!hasRst
                        ? rstMissingHint
                        : isManufacturingRst
                          ? 'Trading & Rental is not used for manufacturing revenue service types. Use Order Review Checklist and BOQ estimation.'
                          : 'No Trading/Rental estimations linked yet. Use Create above to start one.'}
                    </Text>
                  ) : (
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={linkedTradingRentalEstimations}
                      pagination={false}
                      columns={[
                        { title: 'Estimation #', dataIndex: 'estimation_number', key: 'en' },
                        { title: 'Type', dataIndex: 'estimation_type', key: 'type', width: 170 },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          key: 'status',
                          width: 140,
                          render: (v: string) => (v ? <Tag>{v}</Tag> : '—'),
                        },
                        {
                          title: 'Grand total',
                          key: 'gt',
                          render: (_: unknown, r: any) =>
                            `${Number(r.grand_total || 0).toLocaleString()}${r.currency ? ` ${r.currency}` : ''}`,
                        },
                        {
                          title: '',
                          key: 'open',
                          width: 90,
                          render: (_: unknown, r: any) => (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0 }}
                              onClick={() => navigate(`/growth/estimations/trading-rental/${r.id}`)}
                            >
                              Open
                            </Button>
                          ),
                        },
                      ]}
                    />
                  )}
                </Card>

                <Card
                  title="Quotations"
                  loading={quotationsLoading}
                  style={{ marginBottom: 16 }}
                  extra={
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={() => navigate(`/growth/quotations?opportunity_id=${opp.id}`)}
                    >
                      Full list
                    </Button>
                  }
                >
                  {oppQuotations.length === 0 ? (
                    <Text type="secondary">No quotations linked to this opportunity yet.</Text>
                  ) : (
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={oppQuotations}
                      columns={quotationColumns}
                      pagination={false}
                    />
                  )}
                </Card>

                <Card
                  title="Customer orders"
                  loading={salesOrdersLoading}
                  style={{ marginBottom: 16 }}
                  extra={
                    <Button size="small" onClick={() => navigate('/growth/orders')}>
                      All orders
                    </Button>
                  }
                >
                  {oppSalesOrders.length === 0 ? (
                    <Text type="secondary">
                      No customer orders with this opportunity yet. They appear after converting an accepted quotation.
                    </Text>
                  ) : (
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={oppSalesOrders}
                      columns={salesOrderColumns}
                      pagination={false}
                    />
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={10}>
                <Card title="Enterprise Brain" style={{ marginBottom: 16 }}>
                  {oppCheckpoint && !oppCheckpoint.all_pass && (
                    <Alert type="info" showIcon style={{ marginBottom: 8 }} message="Complete pre-quotation checkpoint before sending commercial proposal." />
                  )}
                  {intelligence ? (
                    <>
                      <div style={{ marginBottom: 8 }}>
                        <Text type="secondary">Customer win rate: </Text>
                        <Text strong>{intelligence.customer_win_rate_pct ?? '—'}%</Text>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>Risks</Text>
                        <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
                          {(intelligence.risks ?? []).map((r) => (
                            <li key={r}>{r}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <Text strong>Recommended next actions</Text>
                        <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
                          {(intelligence.recommended_next_actions ?? []).map((a) => (
                            <li key={a}>{a}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  ) : (
                    <Text type="secondary">Loading intelligence…</Text>
                  )}
                </Card>
                <Card
                  title="Activities"
                  loading={actLoading}
                  extra={
                    canScheduleDemo ? (
                      <Button size="small" onClick={() => navigate(`/growth/demos/new?opportunity_id=${id}`)}>
                        Schedule demo
                      </Button>
                    ) : null
                  }
                >
                  <CrmActivityTimeline activities={activities} />
                  {canLogActivity && (
                    <Collapse
                      ghost
                      style={{ marginTop: 8 }}
                      items={[
                        {
                          key: 'log',
                          label: 'Log activity',
                          children: <CrmActivityForm link={{ opportunity_id: opp.id }} />,
                        },
                      ]}
                    />
                  )}
                </Card>
              </Col>
            </Row>

            <Modal
              title="Mark won"
              open={wonOpen}
              onOk={async () => {
                await markWon.mutateAsync({ id: opp.id, win_reason: winReason || undefined });
                setWonOpen(false);
                setWinReason('');
              }}
              onCancel={() => setWonOpen(false)}
              confirmLoading={markWon.isPending}
            >
              <Input
                placeholder="Win reason (optional)"
                value={winReason}
                onChange={(e) => setWinReason(e.target.value)}
              />
            </Modal>

            <Modal
              title="Mark lost"
              open={lostOpen}
              onOk={async () => {
                const r = lossReason.trim();
                if (!r) {
                  message.warning('Loss reason is required');
                  return;
                }
                await markLost.mutateAsync({ id: opp.id, loss_reason: r });
                setLostOpen(false);
                setLossReason('');
              }}
              onCancel={() => setLostOpen(false)}
              confirmLoading={markLost.isPending}
            >
              <Input.TextArea
                rows={3}
                placeholder="Loss reason (required)"
                value={lossReason}
                onChange={(e) => setLossReason(e.target.value)}
              />
            </Modal>

            <Modal
              title="Convert to project"
              open={projectOpen}
              onOk={async () => {
                if (!projectId) return;
                await convertProject.mutateAsync({ id: opp.id, project_id: projectId });
                setProjectOpen(false);
              }}
              onCancel={() => setProjectOpen(false)}
              confirmLoading={convertProject.isPending}
            >
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Select project"
                options={projectOptions.map((p) => ({
                  label: `${p.project_number} — ${p.project_name}`,
                  value: p.id,
                }))}
                value={projectId || undefined}
                onChange={(v) => setProjectId(v)}
              />
            </Modal>
          </>
        )}
      </Spin>
    </div>
  );
};

export default OpportunityDetailPage;
